import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from "../../utils/api";
import PageHeader1 from '../../components/common/PageHeader1';

function DriverIncidents() {
    const { id } = useParams();
    const [driver, setDriver] = useState(null);
    const [incidents, setIncidents] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchDriverDetails = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/drivers/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDriver(response.data.data);
        } catch (error) {
            console.error('Error fetching driver:', error);
            toast.error('Failed to load driver details');
        }
    };

    const fetchDriverIncidents = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get(`accounts/drivers/${id}/incidents`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setIncidents(response.data.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching incidents:', error);
            toast.error('Failed to load driver incidents');
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDriverDetails();
        fetchDriverIncidents();
    }, [id]);

    const handlePrint = (incident) => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Incident Report #${incident.id}</title>
                    <style>
                        body { font-family: Arial, sans-serif; padding: 20px; }
                        .header { text-align: center; margin-bottom: 30px; }
                        .content { margin: 20px 0; }
                        .label { font-weight: bold; margin-right: 10px; }
                        .info-row { margin: 10px 0; }
                        .images { margin-top: 20px; }
                        .image { max-width: 300px; margin: 10px; }
                        .approval { margin-top: 20px; padding: 10px; border-top: 1px solid #ccc; }
                    </style>
                </head>
                <body>
                    <div class="header">
                        <h2>Incident Report #${incident.id}</h2>
                    </div>
                    <div class="content">
                        <div class="info-row">
                            <span class="label">Car Plate:</span>
                            <span>${incident.car_plate}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Driver:</span>
                            <span>${driver?.name || 'N/A'}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Date & Time:</span>
                            <span>${new Date(incident.date_time).toLocaleString()}</span>
                        </div>
                        <div class="info-row">
                            <span class="label">Description:</span>
                            <p>${incident.incident_description}</p>
                        </div>
                        <div class="images">
                            <span class="label">Images:</span><br/>
                            ${incident.images?.map(img => `
                                <img src="${img.url}" alt="${img.file_name}" class="image"/>
                            `).join('') || 'No images'}
                        </div>
                        <div class="approval">
                            <span class="label">Supervisor Approval:</span>
                            <span>${incident.supervisor_approval ? 'Approved' : 'Pending'}</span>
                        </div>
                    </div>
                </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 
                    pagetitle={`Incidents Log for ${driver?.name || 'Driver'}`} 
                    righttitle='Back to Drivers' 
                    link='/accounts/drivers-list' 
                    routebutton={true} 
                />

                <div className="row g-3 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="card-title mb-0">Driver Information</h6>
                            </div>
                            <div className="card-body">
                                {driver && (
                                    <div className="row">
                                        <div className="col-md-6">
                                            <p><strong>Name:</strong> {driver.name}</p>
                                            <p><strong>Contact:</strong> {driver.contact_number}</p>
                                        </div>
                                        <div className="col-md-6">
                                            <p><strong>Status:</strong> 
                                                <span className={`badge ms-2 ${driver.is_active ? 'bg-success' : 'bg-warning'}`}>
                                                    {driver.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </p>
                                            <p><strong>Valet Locations:</strong> {driver.exit_location_names?.join(', ') || 'Not Assigned'}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <h6 className="card-title mb-0">Incident History</h6>
                            </div>
                            <div className="card-body">
                                {loading ? (
                                    <div className="text-center">Loading...</div>
                                ) : incidents.length === 0 ? (
                                    <div className="text-center">No incidents reported for this driver</div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Car Plate</th>
                                                    <th>Date & Time</th>
                                                    <th>Description</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {incidents.map((incident) => (
                                                    <tr key={incident.id}>
                                                        <td>#{incident.id}</td>
                                                        <td>{incident.car_plate}</td>
                                                        <td>{new Date(incident.date_time).toLocaleString()}</td>
                                                        <td>
                                                            {incident.incident_description.length > 50
                                                                ? `${incident.incident_description.substring(0, 50)}...`
                                                                : incident.incident_description
                                                            }
                                                        </td>
                                                        <td>
                                                            <span className={`badge ${incident.supervisor_approval ? 'bg-success' : 'bg-warning'}`}>
                                                                {incident.supervisor_approval ? 'Approved' : 'Pending'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="btn-group">
                                                                <button
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => handlePrint(incident)}
                                                                >
                                                                    <i className="fa fa-print"></i> Print
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default DriverIncidents;
